import { useEffect } from 'react';
import type { CatalogBook, CatalogKeypoint } from '../types.js';

/**
 * MediaSession API para /listen (Fase 23, A.5): expone metadata (libro/capítulo/
 * cover) y los controles de transporte al SO — widget de media, lockscreen y
 * botones de auriculares. Imprescindible para la app nativa (audio en background,
 * Bloque D) y un plus en la web de escritorio/Android.
 *
 * Lo consume `ListenProvider` (dueño del único `<audio>`), al lado de
 * `useWakeLock`/`useSleepTimer`. Best-effort: si el UA no soporta MediaSession
 * (Safari web viejo, etc.) el hook no hace nada y la reproducción sigue igual.
 */

const SKIP_SECONDS = 15;
const ALL_ACTIONS: readonly MediaSessionAction[] = [
  'play',
  'pause',
  'seekforward',
  'seekbackward',
  'seekto',
  'nexttrack',
  'previoustrack',
];

interface UseMediaSessionArgs {
  book: CatalogBook | null;
  chapter: CatalogKeypoint | null;
  playing: boolean;
  pos: number;
  duration: number;
  play: () => void;
  pause: () => void;
  seek: (sec: number) => void;
  skip: (delta: number) => void;
  hasNext: boolean;
  hasPrev: boolean;
  goNextChapter: () => void;
  goPrevChapter: () => void;
}

export function useMediaSession(args: UseMediaSessionArgs): void {
  const {
    book,
    chapter,
    playing,
    pos,
    duration,
    play,
    pause,
    seek,
    skip,
    hasNext,
    hasPrev,
    goNextChapter,
    goPrevChapter,
  } = args;

  // 1) Metadata: título de capítulo + autor + libro (+ cover si hay). Se reasigna
  //    al cambiar de capítulo/libro; sin sesión abierta, se limpia.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!book || !chapter) {
      navigator.mediaSession.metadata = null;
      return;
    }
    navigator.mediaSession.metadata = new MediaMetadata({
      title: chapter.title,
      artist: book.author,
      album: book.title,
      ...(book.coverUrl ? { artwork: [{ src: book.coverUrl }] } : {}),
    });
  }, [book, chapter]);

  // 2) Estado play/paused para el widget del SO.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
  }, [playing]);

  // 3) Posición (barra de progreso del lockscreen). Best-effort: algunos UA
  //    validan estricto (position ≤ duration finita) y lanzan si no cuadra.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (typeof navigator.mediaSession.setPositionState !== 'function') return;
    if (!Number.isFinite(duration) || duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        position: Math.min(Math.max(pos, 0), duration),
        playbackRate: 1,
      });
    } catch {
      /* UA estricto → ignorar, no es crítico */
    }
  }, [pos, duration]);

  // 4) Handlers de transporte (lockscreen/auriculares). Se re-registran cuando
  //    cambian las acciones o la disponibilidad de capítulo previo/siguiente.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null): void => {
      try {
        ms.setActionHandler(action, handler);
      } catch {
        /* acción no soportada por este UA → ignorar */
      }
    };
    set('play', () => play());
    set('pause', () => pause());
    set('seekforward', (d) => skip(d.seekOffset ?? SKIP_SECONDS));
    set('seekbackward', (d) => skip(-(d.seekOffset ?? SKIP_SECONDS)));
    set('seekto', (d) => {
      if (typeof d.seekTime === 'number') seek(d.seekTime);
    });
    // Prev/next saltan de capítulo (encadenan cruzando audiolibros). Sólo se
    // ofrecen cuando hay a dónde ir, para que el SO no muestre el botón muerto.
    set('nexttrack', hasNext ? () => goNextChapter() : null);
    set('previoustrack', hasPrev ? () => goPrevChapter() : null);
    return () => {
      for (const a of ALL_ACTIONS) set(a, null);
    };
  }, [play, pause, seek, skip, hasNext, hasPrev, goNextChapter, goPrevChapter]);
}
